/**
 * aai-header.js — sticky state, hover-intent mega panels, mobile drawer, live cart count.
 * All navigation works without this module (details/summary, plain links).
 */
class AaiHeader extends HTMLElement {
  #openTimer = 0;
  #closeTimer = 0;

  connectedCallback() {
    this.details = [...this.querySelectorAll('[data-aai-nav-details]')];
    this.drawer = this.querySelector('[data-aai-drawer]');
    this.openBtn = this.querySelector('[data-aai-drawer-open]');
    this.countEl = this.querySelector('[data-aai-cart-count]');
    this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.#bindPanels();
    this.#bindDrawer();
    this.#bindScroll();
    this.#bindCart();
    this.#setOffset();
    this.resizeObserver = new ResizeObserver(() => {
      this.#setOffset();
      const open = this.details.find((d) => d.open);
      if (open) this.#placePanel(open);
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    document.removeEventListener('click', this.#onDocClick);
    document.removeEventListener('keydown', this.#onKey);
    document.removeEventListener('cart:update', this.#onCartUpdate);
    window.removeEventListener('scroll', this.#onScroll);
  }

  /* --- header height as a CSS variable, for anchors and sticky offsets --- */
  #setOffset() {
    const bar = this.querySelector('.aai-header__bar');
    if (bar) document.documentElement.style.setProperty('--aai-header-height', `${bar.offsetHeight}px`);
  }

  /* --- mega panels --- */
  // The panel is meant to span the header (left/right: 0 against .aai-header). If some other
  // stylesheet turns the menu item into its containing block, the panel shrinks to the item's
  // width and its links spill over the page. Measure where it landed and line it up with the header.
  #placePanel(d) {
    const panel = d.querySelector('.aai-header__panel');
    if (!panel) return;
    panel.style.left = panel.style.right = panel.style.top = panel.style.width = '';
    const anchor = panel.offsetParent;
    if (!anchor || anchor === this) return;
    const header = this.getBoundingClientRect();
    const box = anchor.getBoundingClientRect();
    panel.style.left = `${header.left - box.left - anchor.clientLeft}px`;
    panel.style.right = 'auto';
    panel.style.top = `${header.bottom - box.top - anchor.clientTop}px`;
    panel.style.width = `${header.width}px`;
  }

  #bindPanels() {
    this.details.forEach((d) => {
      const summary = d.querySelector('summary');
      d.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', d.open ? 'true' : 'false');
        if (d.open) {
          this.details.forEach((o) => o !== d && (o.open = false));
          this.#placePanel(d);
        }
      });
      summary.setAttribute('aria-expanded', 'false');

      d.addEventListener('pointerenter', () => {
        if (!this.hoverCapable.matches) return;
        clearTimeout(this.#closeTimer);
        this.#openTimer = setTimeout(() => (d.open = true), 80);
      });
      d.addEventListener('pointerleave', () => {
        if (!this.hoverCapable.matches) return;
        clearTimeout(this.#openTimer);
        this.#closeTimer = setTimeout(() => (d.open = false), 160);
      });
      // With hover already opening the panel, a click on the summary follows the parent link is not
      // available (summary has no href), so keep click as toggle for keyboard/touch users.
    });
    document.addEventListener('click', this.#onDocClick);
    document.addEventListener('keydown', this.#onKey);
  }

  #onDocClick = (e) => {
    if (!this.contains(e.target)) this.#closeAll();
  };

  #onKey = (e) => {
    if (e.key !== 'Escape') return;
    const open = this.details.find((d) => d.open);
    if (open) {
      open.open = false;
      open.querySelector('summary')?.focus();
    }
  };

  #closeAll() {
    this.details.forEach((d) => (d.open = false));
  }

  /* --- mobile drawer --- */
  #bindDrawer() {
    if (!this.drawer || !this.openBtn) return;
    const open = () => {
      if (typeof this.drawer.showModal !== 'function') return;
      this.drawer.showModal();
      this.openBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('aai-scroll-lock');
    };
    const close = () => {
      this.drawer.close();
    };
    this.openBtn.addEventListener('click', open);
    this.drawer.querySelectorAll('[data-aai-drawer-close]').forEach((b) => b.addEventListener('click', close));
    this.drawer.addEventListener('click', (e) => {
      // backdrop click: the dialog element itself, not its inner wrapper
      if (e.target === this.drawer) close();
    });
    this.drawer.addEventListener('close', () => {
      this.openBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('aai-scroll-lock');
      this.openBtn.focus();
    });
    // Close the drawer when the viewport grows past the mobile breakpoint
    window.matchMedia('(min-width: 64em)').addEventListener('change', (m) => m.matches && this.drawer.open && close());
  }

  /* --- sticky compact state --- */
  #bindScroll() {
    this.#onScroll();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  #onScroll = () => {
    const scrolled = window.scrollY > 12;
    if (scrolled !== this.classList.contains('is-scrolled')) {
      this.classList.toggle('is-scrolled', scrolled);
    }
  };

  /* --- cart count --- */
  #bindCart() {
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  #onCartUpdate = async (e) => {
    let count = e?.detail?.data?.itemCount;
    if (typeof count !== 'number') {
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        count = (await res.json()).item_count;
      } catch (err) {
        return;
      }
    }
    if (!this.countEl) return;
    this.countEl.textContent = String(count);
    this.countEl.classList.toggle('is-empty', count === 0);
    this.countEl.classList.remove('is-bumped');
    void this.countEl.offsetWidth;
    this.countEl.classList.add('is-bumped');
  };
}

if (!customElements.get('aai-header')) customElements.define('aai-header', AaiHeader);
